import {
  COMPLIANCE_DOCUMENT_STATUS_LABELS,
  type ComplianceDocument,
  EXPIRY_STATUS_LABELS,
  EXPIRY_STATUSES,
  type ExpiryStatusCode,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Select,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FileBadge, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { EXPIRY_TONES, expiryText } from "../../../../features/compliance/compliance-values.js";
import { DocumentFormDialog } from "../../../../features/compliance/document-dialogs.js";
import {
  type DocumentFilters,
  useComplianceDocuments,
  useComplianceSummary,
  useComplianceTypes,
} from "../../../../features/compliance/queries.js";
import { useMaintenancePermissions } from "../../../../features/maintenance/queries.js";
import { MaintenancePage, StationFilterField } from "../../../../features/maintenance/shared.js";
import { StatTile } from "../../../../features/reports/shared.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDate } from "../../../../lib/format.js";

const searchSchema = z.object({
  station: z.string().uuid().optional().catch(undefined),
  type: z.string().uuid().optional().catch(undefined),
  expiry: z.enum(EXPIRY_STATUSES).optional().catch(undefined),
  status: z.enum(["ACTIVE", "SUPERSEDED", "ARCHIVED", "ALL"]).optional().catch(undefined),
  q: z.string().max(80).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/uyum/belgeler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DocumentsRoute,
});

/**
 * Uyum belgeleri (Prompt 23 web, `GET /compliance/documents` imleçli, bitişi en yakın önce): özet (süresi dolan / yaklaşan,
 * doğrulanmamış tür, açık / geciken faaliyet), süzgeçler URL'de. Yeni belge türün kapsamına göre şirket / istasyon / varlık
 * seçtirir.
 */
function DocumentsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useMaintenancePermissions();
  const search = Route.useSearch();
  const [creating, setCreating] = useState(false);
  return (
    <MaintenancePage
      title={t("menu.complianceDocuments")}
      description={t("compliance.documents.description")}
      requirement="compliance"
      actions={
        permissions.canManageCompliance ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("compliance.documents.new")}
          </Button>
        ) : null
      }
    >
      <Summary stationId={search.station ?? null} />
      <DocumentList />
      {creating ? (
        <DocumentFormDialog
          open
          onOpenChange={setCreating}
          mode="create"
          document={null}
          defaultStationId={search.station ?? null}
        />
      ) : null}
    </MaintenancePage>
  );
}

function Summary({ stationId }: { stationId: string | null }): ReactElement | null {
  const { t } = useTranslation();
  const summary = useComplianceSummary(stationId);
  if (summary.error) return <Alert tone="danger">{errorMessage(summary.error)}</Alert>;
  if (!summary.data) return null;
  const data = summary.data;
  return (
    <Card className="grid gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6">
      <StatTile label={EXPIRY_STATUS_LABELS.EXPIRED} value={formatCount(data.expired)} />
      <StatTile label={EXPIRY_STATUS_LABELS.EXPIRING} value={formatCount(data.expiring)} />
      <StatTile label={EXPIRY_STATUS_LABELS.VALID} value={formatCount(data.valid)} />
      <StatTile label={t("compliance.summary.unverifiedTypes")} value={formatCount(data.unverifiedTypes)} />
      <StatTile
        label={t("compliance.summary.openActions")}
        value={formatCount(data.openActions)}
        hint={
          data.overdueActions > 0
            ? t("compliance.summary.overdue", { count: data.overdueActions })
            : undefined
        }
      />
      <StatTile
        label={t("compliance.summary.plannedInspections")}
        value={formatCount(data.plannedInspections)}
      />
    </Card>
  );
}

function DocumentList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const types = useComplianceTypes();
  const filters: DocumentFilters = {
    stationId: search.station ?? null,
    typeId: search.type ?? null,
    expiry: search.expiry ?? null,
    status: search.status ?? "ACTIVE",
    search: search.q ?? "",
  };
  const [text, setText] = useState(filters.search);
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useComplianceDocuments(filters, pages.cursor);
  const setFilters = (next: DocumentFilters) =>
    void navigate({
      to: ".",
      search: {
        ...(next.stationId ? { station: next.stationId } : {}),
        ...(next.typeId ? { type: next.typeId } : {}),
        ...(next.expiry ? { expiry: next.expiry } : {}),
        ...(next.status !== "ACTIVE" ? { status: next.status } : {}),
        ...(next.search ? { q: next.search } : {}),
      } as never,
      replace: true,
    });

  const columns = useMemo<DataTableColumn<ComplianceDocument>[]>(
    () => [
      {
        id: "type",
        header: t("compliance.fields.type"),
        hideable: false,
        cell: (row) => (
          <span className="grid min-w-52 gap-0.5">
            <Link
              to="/uyum/belgeler/$id"
              params={{ id: row.id }}
              className="font-medium underline underline-offset-4"
            >
              {row.type.name}
            </Link>
            <span className="text-xs text-muted-foreground">
              {row.documentNo ?? t("compliance.documents.noNumber")}
              {row.type.verified ? "" : ` · ${t("compliance.types.unverifiedBadge")}`}
            </span>
          </span>
        ),
      },
      {
        id: "scope",
        header: t("compliance.fields.scope"),
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="whitespace-nowrap">{row.stationCode ?? row.companyName}</span>
            {row.asset ? <span className="text-xs text-muted-foreground">{row.asset.name}</span> : null}
          </span>
        ),
      },
      {
        id: "expiresOn",
        header: t("compliance.fields.expiresOn"),
        cell: (row) => (
          <span className="grid justify-items-start gap-1">
            <span className="tabular whitespace-nowrap">{formatDate(row.expiresOn)}</span>
            {row.status === "ACTIVE" ? (
              <StatusBadge tone={EXPIRY_TONES[row.expiry.status]}>{expiryText(row.expiry)}</StatusBadge>
            ) : (
              <StatusBadge>{COMPLIANCE_DOCUMENT_STATUS_LABELS[row.status]}</StatusBadge>
            )}
          </span>
        ),
      },
      {
        id: "nextReminder",
        header: t("compliance.fields.nextReminderOn"),
        cell: (row) => (
          <span className="tabular whitespace-nowrap">{formatDate(row.expiry.nextReminderOn)}</span>
        ),
      },
      {
        id: "responsible",
        header: t("compliance.fields.responsible"),
        cell: (row) => row.responsibleUser?.name ?? "—",
      },
      {
        id: "issuedOn",
        header: t("compliance.fields.issuedOn"),
        defaultVisible: false,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDate(row.issuedOn)}</span>,
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("menu.complianceDocuments")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      filters={
        <>
          <StationFilterField
            value={filters.stationId}
            onChange={(stationId) => setFilters({ ...filters, stationId })}
          />
          <FormField label={t("compliance.fields.type")} className="w-52">
            {(control) => (
              <Select
                {...control}
                value={filters.typeId ?? ""}
                onChange={(event) => setFilters({ ...filters, typeId: event.target.value || null })}
              >
                <option value="">{t("common.all")}</option>
                {(types.data?.items ?? []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("compliance.fields.expiry")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={filters.expiry ?? ""}
                onChange={(event) =>
                  setFilters({ ...filters, expiry: (event.target.value || null) as ExpiryStatusCode | null })
                }
              >
                <option value="">{t("common.all")}</option>
                {EXPIRY_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {EXPIRY_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("compliance.fields.recordStatus")} className="w-40">
            {(control) => (
              <Select
                {...control}
                value={filters.status}
                onChange={(event) =>
                  setFilters({ ...filters, status: event.target.value as DocumentFilters["status"] })
                }
              >
                <option value="ACTIVE">{COMPLIANCE_DOCUMENT_STATUS_LABELS.ACTIVE}</option>
                <option value="SUPERSEDED">{COMPLIANCE_DOCUMENT_STATUS_LABELS.SUPERSEDED}</option>
                <option value="ARCHIVED">{COMPLIANCE_DOCUMENT_STATUS_LABELS.ARCHIVED}</option>
                <option value="ALL">{t("common.all")}</option>
              </Select>
            )}
          </FormField>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setFilters({ ...filters, search: text.trim() });
            }}
          >
            <FormField label={t("common.search")} className="w-52">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={text}
                  placeholder={t("compliance.documents.searchPlaceholder")}
                  onChange={(event) => setText(event.target.value)}
                />
              )}
            </FormField>
          </form>
        </>
      }
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<FileBadge />}
          title={t("compliance.documents.emptyTitle")}
          description={t("compliance.documents.emptyDescription")}
        />
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
